// LocalVerticalResizeBorder.jsx
// Context used by the FileViewer components to share state and functions.

// Copyright HS Analysis GmbH, 2020
// Authors: Viktor Eberhardt, Valentin Haas, Nina Sebold, Jakob Burghardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// Material UI imports
import withStyles from "@mui/styles/withStyles";

// define the component's styling
const styles = () => ({
  resizesBorder: {
    height: "100%",
    width: "100%",
    cursor: "col-resize",
  },
  //invisible over whole screen, so mouse is always tracked
  overlayBorder: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 9999999,
    cursor: "col-resize",
  },
  "@media (pointer: coarse)": {
    resizesBorder: {
      width: "44px", // Increase width for touch devices
    },
  },
});

class LocalVerticalResizeBorder extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      resizeMode: false,
      borderWidth: 16,
      newWidth: props.targetWidth,
    };
  }

  /**
   * Normalizes the event to extract the relevant data for both touch and mouse events.
   * @param {Event} e - The event object (MouseEvent or TouchEvent).
   * @returns {Object} - The normalized event with pageX property.
   */
  normalizeEvent = (e) => {
    if (e.touches) {
      // Touch event
      return e.touches[0];
    }
    // Mouse event
    return e;
  };

  /**
   * Handles the start of the resize operation (mouse down or touch start).
   * Initializes the resize mode and sets the starting position.
   * @param {Event} e - The event object (MouseEvent or TouchEvent).
   */
  handleStart = (e) => {
    const event = this.normalizeEvent(e);
    this.setState({
      resizeMode: true,
      lastPageX: event.pageX,
      initPageX: event.pageX,
      newWidth: this.props.targetWidth, // Reset newWidth to the current targetWidth
    });
  };

  /**
   * Handles the movement during the resize operation (mouse move or touch move).
   * Calculates the new width and updates the state and parent component.
   * @param {Event} e - The event object (MouseEvent or TouchEvent).
   */
  handleMove = (e) => {
    if (!this.state.resizeMode) return;

    const event = this.normalizeEvent(e);
    const deltaX = this.props.leftBorder
      ? this.state.lastPageX - event.pageX
      : event.pageX - this.state.lastPageX;
    const newWidth = this.state.newWidth + deltaX;

    if (this.props.min === undefined || newWidth > this.props.min) {
      this.props.resizeWidth(newWidth);
    } else {
      this.props.resizeWidth(this.props.min);
    }

    this.setState({ lastPageX: event.pageX, newWidth });
  };

  /**
   * Handles the end of the resize operation (mouse up or touch end).
   * Resets the resize mode and triggers the resize complete callback.
   */
  handleEnd = () => {
    if (!this.state.resizeMode) return;

    this.setState({
      resizeMode: false,
      lastPageX: null, // Reset lastPageX to ensure proper behavior on the next drag
    });

    if (this.props.handleResizeComplete) {
      this.props.handleResizeComplete(this.state.newWidth);
    }
  };

  /**
   * Adds global event listeners for mouse and touch events when the component is mounted.
   */
  componentDidMount() {
    // Add non-passive touchmove listener
    document.addEventListener("touchmove", this.handleMove, { passive: false });
    document.addEventListener("mousemove", this.handleMove); // Ensure mousemove works globally
    document.addEventListener("mouseup", this.handleEnd); // Ensure mouseup works globally
    document.addEventListener("touchend", this.handleEnd); // Ensure touchend works globally
  }

  /**
   * Removes global event listeners for mouse and touch events when the component is unmounted.
   */
  componentWillUnmount() {
    // Remove the listeners to avoid memory leaks
    document.removeEventListener("touchmove", this.handleMove);
    document.removeEventListener("mousemove", this.handleMove);
    document.removeEventListener("mouseup", this.handleEnd);
    document.removeEventListener("touchend", this.handleEnd);
  }

  /**
   * Renders the resize border component.
   * @returns {JSX.Element} - The rendered component.
   */
  render() {
    const { classes } = this.props;
    return (
      <div
        style={{
          height: "100%",
          width: this.state.borderWidth,
          zIndex: 1,
        }}
      >
        <div
          className={
            this.state.resizeMode
              ? classes.overlayBorder
              : classes.resizesBorder
          }
          onMouseDown={this.handleStart}
          onTouchStart={this.handleStart}
        ></div>
      </div>
    );
  }
}

// define the component's interface
LocalVerticalResizeBorder.propTypes = {
  classes: PropTypes.object.isRequired,
  handleResizeComplete: PropTypes.func,
  leftBorder: PropTypes.bool,
  min: PropTypes.number,
  resizeWidth: PropTypes.func.isRequired,
  targetWidth: PropTypes.number.isRequired,
};

export default withStyles(styles)(LocalVerticalResizeBorder);
